import React, { createContext, useContext, useCallback, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  secureTokenStore,
  useCurrentUserStore,
  useAuthCredentialsStore,
  useListingContactsStore,
  useSuggestedContactsFiltersStore,
} from '@store'
import { navigationRef } from '../navigationRef'
import { api } from '@api'
import { purchaseManager } from '../utils/purchases/purchaseManager'
import { getCurrentPushToken, setCurrentPushToken } from '../utils/pushToken'

interface AuthContextType {
  signOut: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { resetUserID } = useCurrentUserStore()
  const { resetAuthCredentials } = useAuthCredentialsStore()
  const queryClient = useQueryClient()

  const signOut = useCallback(async () => {
    try {
      // Stop this phone from receiving the notifications of the account
      // signing out (best-effort, while the session is still valid).
      const pushToken = getCurrentPushToken()
      if (pushToken) {
        try {
          await api.post('/devices/unregister', { token: pushToken })
        } catch {
          // Best-effort
        }
        setCurrentPushToken(null)
      }

      const refreshToken = secureTokenStore.getRefreshToken()
      if (refreshToken) {
        try {
          await api.post('/authentication/revoke-token', { refreshToken })
        } catch {
          // Best-effort revocation
        }
      }

      // Detach the account from the purchase SDK so a later sign-in on the same
      // browser is not attributed to the previous user. No-op on native (stub).
      await purchaseManager.logOut()

      resetUserID()
      resetAuthCredentials()
      // The contact filters, search text and Privé / Pro tab of the account
      // signing out must not apply to the next one on this device.
      useListingContactsStore.getState().reset()
      useSuggestedContactsFiltersStore.getState().reset()
      await secureTokenStore.clearTokens()
      queryClient.clear()

      // Reset to the Login screen via in-app navigation (web + native). We must
      // NOT do a full-page `window.location` reload on web: it re-boots the app,
      // and a background 401 on the auth screen re-triggers signOut → reload → loop.
      if (navigationRef.isReady()) {
        navigationRef.reset({
          index: 0,
          routes: [{ name: 'Auth', params: { screen: 'Login' } } as never],
        })
      }
    } catch (error) {
      console.error('SignOut error:', error)
    }
  }, [resetUserID, resetAuthCredentials, queryClient])

  return <AuthContext.Provider value={{ signOut }}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
